// Package imports:
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faPlusCircle, faMinusCircle } from '@fortawesome/pro-solid-svg-icons';
import Spinner from 'react-bootstrap/Spinner';
// Component imports:
import Table from '../../ui-elements/Table/Table';
import Button from '../../ui-elements/Button/Button';
import Link from '../../ui-elements/Link/Link';
import HistoryButtonDropDown from '../HistoryButtonDropDown/HistoryButtonDropDown';
import Alert from '../../ui-elements/Alert/Alert';
import Loading from '../../ui-elements/Loading/Loading';
import ErrorAlert from '../ErrorAlert/ErrorAlert';
import ReikningarModal from './ReikningarModal';
// Service imports:
import { formatNumber, getFullMonthAndYear, getTableDate } from '../../services/utils';
import { GET_KELDAN_API_URL } from '../../services/config';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { IKeldanApiAllInvoicesModel, IInvoiceReference, IInvoiceUser, IInvoiceContainer, InvoiceModalInfo, IInvoice, InvoiceIdAndUserIdToIInvoiceUserDetailsMap, InvoiceIdToInvoiceCustomerResult, getUserDetailsMapId } from '../../types/MyKeldaTypes';
import { Fetched } from '../../types/Types';

type SubTableOptions = 'Notendur' | 'Tilvisun';
type ReikningarToSubTableMap = {
    [T in string]?: SubTableOptions | undefined
}

type InvoiceIdToIInvoiceUserMap = {
    [T in string]: IInvoiceUser[] | undefined | Error
}

type InvoiceIdToIInvoiceReferenceMap = {
    [T in string]: IInvoiceReference[] | undefined | Error
}

const Reikningar: React.FC= () => {
    // Local states:
    const [ invoiceModalInfo, setInvoiceModalInfo ] = useState<InvoiceModalInfo | null>(null);
    const [ reikningarToSubTableMap, setReikningarToSubTableMap ] = useState<ReikningarToSubTableMap>({});
    // Main table data:
    const [ invoiceContainers, setInvoiceContainers ] = useState<Fetched<IInvoiceContainer[]>>(null);
    // Subtable data:
    const [ invoiceIdToInvoiceUserMap, setInvoiceIdToInvoiceUserMap ] = useState<InvoiceIdToIInvoiceUserMap>({});
    const [ invoiceIdToInvoiceReferenceMap, setInvoiceIdToInvoiceReferenceMap ] = useState<InvoiceIdToIInvoiceReferenceMap>({});
    // Modal data:
    const [ invoiceUserDetailsMap, setInvoiceUserDetailsMap ] = useState<InvoiceIdAndUserIdToIInvoiceUserDetailsMap>({});
    const [ invoiceCustomerResultMap, setInvoiceCustomerResultMap ] = useState<InvoiceIdToInvoiceCustomerResult>({});

    // Helper functions:
    const toggleSubTableForInvoiceId = (invoiceId: number, clickedButton: SubTableOptions) => {
        const currentSubTableForDatumId = reikningarToSubTableMap[invoiceId];
        if (clickedButton === currentSubTableForDatumId) {
            setReikningarToSubTableMap({
                ...reikningarToSubTableMap,
                [invoiceId]: undefined
            })
        } else {
            setReikningarToSubTableMap({
                ...reikningarToSubTableMap,
                [invoiceId]: clickedButton
            })
        }
    }

    // Get inital invoiceContainers.
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${GET_KELDAN_API_URL()}/MyKelda/ApiGetAllInvoices`);
                if (response.ok) {
                    const responseBody: IKeldanApiAllInvoicesModel = await response.json();
                    if (responseBody.invoiceContainers === null) {
                        setInvoiceContainers(new Error(responseBody.error ?? 'Error occured'));
                    } else {
                        setInvoiceContainers(responseBody.invoiceContainers);
                    }
                } else {
                    setInvoiceContainers(new Error(ErrorMessages.RequestFailed));
                }
            } catch (e) {
                setInvoiceContainers(new Error(ErrorMessages.NetworkError));
            }
        }
        fetchData();
    }, []);

    // Fetch InvoiceUser and InvoiceReference data
    useEffect(() => {
        const fetchUserOrReferenceData = async () => {
            let valuesChanged = false;
            const newInvoiceIdToInvoiceUserMap = {...invoiceIdToInvoiceUserMap};
            const newInvoiceIdToInvoiceReferenceMap = {...invoiceIdToInvoiceReferenceMap};
            const values = Object.entries(reikningarToSubTableMap);
            for (const [ invoiceId, subTableOption ] of values) {
                if (subTableOption === 'Notendur') {
                    if (invoiceIdToInvoiceUserMap[invoiceId] === undefined) {
                        valuesChanged = true;
                        try {
                            const response = await fetch(`${GET_KELDAN_API_URL()}/MyKelda/InvoiceByUser?id=${invoiceId}`);
                            if (response.ok) {
                                const responseBody = await response.json();
                                newInvoiceIdToInvoiceUserMap[invoiceId] = responseBody;
                            } else {
                                newInvoiceIdToInvoiceUserMap[invoiceId] = new Error(ErrorMessages.RequestFailed);
                            }
                        } catch (e) {
                            newInvoiceIdToInvoiceUserMap[invoiceId] = new Error(ErrorMessages.NetworkError);
                        }
                    }
                }
                else if (subTableOption === 'Tilvisun') {
                    if (invoiceIdToInvoiceReferenceMap[invoiceId] === undefined) {
                        valuesChanged = true;
                        try {
                            const response = await fetch(`${GET_KELDAN_API_URL()}/MyKelda/InvoiceByReference?id=${invoiceId}`);
                            if (response.ok) {
                                const responseBody = await response.json();
                                newInvoiceIdToInvoiceReferenceMap[invoiceId] = responseBody;
                            } else {
                                newInvoiceIdToInvoiceReferenceMap[invoiceId] = new Error(ErrorMessages.RequestFailed);
                            }
                        } catch (e) {
                            newInvoiceIdToInvoiceReferenceMap[invoiceId] = new Error(ErrorMessages.NetworkError);
                        }
                    }
                }
            }

            if (valuesChanged) {
                setInvoiceIdToInvoiceUserMap(newInvoiceIdToInvoiceUserMap);
                setInvoiceIdToInvoiceReferenceMap(newInvoiceIdToInvoiceReferenceMap);
            }
        }
        fetchUserOrReferenceData();
    }, [ reikningarToSubTableMap ]);

    // Fetch modal data:
    useEffect(() => {
        if (invoiceModalInfo === null) return;
        // Reikningur
        if (invoiceModalInfo.modalType === 'Reikningur') {
            const { invoice } = invoiceModalInfo;
            if (invoiceCustomerResultMap[invoice.id] === undefined) {
                fetchCustomerResult(invoice);
            }
        }
        // Notendur
        else if (invoiceModalInfo.modalType === 'Notendur') {
            const { invoiceUser } = invoiceModalInfo;
            if (invoiceUserDetailsMap[getUserDetailsMapId(invoiceUser)] === undefined) {
                fetchUserDetails(invoiceUser);
            }
        }
        // Tilvisun data is already fetched.
    }, [ invoiceModalInfo ]);

    const fetchUserDetails = async (invoiceUser: IInvoiceUser) => {
        try {
            const response = await fetch(`${GET_KELDAN_API_URL()}/MyKelda/InvoiceByUserDetails?id=${invoiceUser.id}&userId=${invoiceUser.userId}`);
            if (response.ok) {
                const responseBody = await response.json();
                setInvoiceUserDetailsMap({
                    ...invoiceUserDetailsMap,
                    [getUserDetailsMapId(invoiceUser)]: responseBody
                });
            } else {
                setInvoiceUserDetailsMap({
                    ...invoiceUserDetailsMap,
                    [getUserDetailsMapId(invoiceUser)]: new Error(ErrorMessages.RequestFailed)
                });
            }
        } catch (e) {
            setInvoiceUserDetailsMap({
                ...invoiceUserDetailsMap,
                [getUserDetailsMapId(invoiceUser)]: new Error(ErrorMessages.NetworkError)
            });
        }
    }

    const fetchCustomerResult = async (invoice: IInvoice) => {
        try {
            const response = await fetch(`${GET_KELDAN_API_URL()}/MyKelda/InvoiceByCustomer?id=${invoice.id}`);
            if (response.ok) {
                const responseBody = await response.json();
                // Sometimes response is ok and has body but is still error.
                if (responseBody?.result !== undefined) {
                    setInvoiceCustomerResultMap({
                        ...invoiceCustomerResultMap,
                        [invoice.id]: new Error(ErrorMessages.OtherShort)
                    });
                } else {
                    setInvoiceCustomerResultMap({
                        ...invoiceCustomerResultMap,
                        [invoice.id]: responseBody
                    });
                }
            } else {
                setInvoiceCustomerResultMap({
                    ...invoiceCustomerResultMap,
                    [invoice.id]: new Error(ErrorMessages.RequestFailed)
                });
            }
        } catch (e) {
            setInvoiceCustomerResultMap({
                ...invoiceCustomerResultMap,
                [invoice.id]: new Error(ErrorMessages.NetworkError)
            });
        }
    }

    return (
        <div className='KCL_Reikningar'>
            <ReikningarModal
                invoiceModalInfo={invoiceModalInfo}
                invoiceUserDetailsMap={invoiceUserDetailsMap}
                invoiceCustomerResultMap={invoiceCustomerResultMap}
                onHide={() => setInvoiceModalInfo(null)}
                />
            <div className='shell'>
                <div className='section'>
                    <div className='section__title'>
                        <h1>Reikningar</h1>
                        <div>
                            <HistoryButtonDropDown />
                        </div>
                    </div>
                    <div className='section__table'>
                        {(invoiceContainers === null)
                        ? <Loading />
                        : (invoiceContainers instanceof Error)
                        ? <Alert type='error' headText={invoiceContainers.message} />
                        : (
                            <Table
                                data={invoiceContainers}
                                tableSize='lg'
                                className='main-table'
                                rowClassName={({invoice}) => (reikningarToSubTableMap[invoice.id] === undefined) ? undefined : 'table__opened-row'}
                                columns={[{
                                    title: 'Mánuður',
                                    dataAccessor: ({invoice}) => getFullMonthAndYear(new Date(invoice.fromDate)),
                                    textAlign: 'left',
                                },{
                                    title: 'Frá',
                                    dataAccessor: ({invoice}) => getTableDate(invoice.fromDate.toString(), 'DD/MM/YYYY HH:MM', '.'),
                                    textAlign: 'left',
                                },{
                                    title: 'Til',
                                    dataAccessor: ({invoice}) => getTableDate(invoice.toDate.toString(), 'DD/MM/YYYY HH:MM', '.'),
                                    textAlign: 'left',
                                },{
                                    title: 'Greitt',
                                    dataAccessor: ({invoice}) => formatNumber(invoice.amountTotal, '-', 0, ' kr.'),
                                    textAlign: 'left',
                                },{
                                    title: 'Sundurliðun',
                                    textAlign: 'left',
                                    dataAccessor: ({invoice}) => (
                                        <div
                                            className='table__openSubTable-button'
                                            onClick={() => toggleSubTableForInvoiceId(invoice.id, 'Notendur')}
                                        >
                                            {
                                                // Check if closed:
                                                (reikningarToSubTableMap[invoice.id] !== 'Notendur')
                                                ? (
                                                    <FontAwesomeIcon className={'fa-icon'} icon={faPlusCircle} />
                                                )
                                                // Check if loading
                                                : (invoiceIdToInvoiceUserMap[invoice.id] === undefined)
                                                ? (
                                                    <Spinner className='loading-spinner' animation="border" />
                                                )
                                                // Else open.
                                                : (
                                                    <FontAwesomeIcon className={'fa-icon'} icon={faMinusCircle} />
                                                )
                                            }
                                            <span className='table__sundurlidun-button-text'>
                                                <Link>
                                                    {'Notendur'}
                                                </Link>
                                            </span>
                                        </div>
                                    ),
                                },{
                                    title: 'Sundurliðun',
                                    textAlign: 'left',
                                    dataAccessor: ({invoice}) => (
                                        <div
                                            className='table__openSubTable-button'
                                            onClick={() => toggleSubTableForInvoiceId(invoice.id, 'Tilvisun')}
                                        >
                                            {
                                                // Check if closed:
                                                (reikningarToSubTableMap[invoice.id] !== 'Tilvisun')
                                                ? (
                                                    <FontAwesomeIcon className={'fa-icon'} icon={faPlusCircle} />
                                                )
                                                // Check if loading
                                                : (invoiceIdToInvoiceReferenceMap[invoice.id] === undefined)
                                                ? (
                                                    <Spinner className='loading-spinner' animation="border" />
                                                )
                                                // Else open.
                                                : (
                                                    <FontAwesomeIcon className={'fa-icon'} icon={faMinusCircle} />
                                                )
                                            }
                                            <span className='table__sundurlidun-button-text'>
                                                <Link>
                                                    {'Tilvísun'}
                                                </Link>
                                            </span>
                                        </div>
                                    ),
                                },{
                                    title: 'Reikningar',
                                    textAlign: 'center',
                                    dataAccessor: ({invoice}) => (
                                        (new Date(invoice.toDate).getMonth() === new Date().getMonth()
                                        && new Date(invoice.toDate).getFullYear() === new Date().getFullYear()) 
                                        ? null
                                        : <div>
                                            <Button
                                                buttonType='secondary'
                                                size='sm'
                                                showLoader={(
                                                    invoiceModalInfo?.modalType === 'Reikningur'
                                                    && invoiceModalInfo.invoice.id === invoice.id
                                                    && invoiceCustomerResultMap[invoice.id] === undefined
                                                )}
                                                onClick={() => {
                                                    setInvoiceModalInfo({
                                                        modalType: 'Reikningur',
                                                        invoice
                                                    })
                                                }}
                                            >
                                                {(invoice.amountTotal < 0)
                                                ? 'Bakfærsla'
                                                : 'Reikningur'}
                                            </Button>
                                        </div>
                                    ),
                                },{
                                    title: '',
                                    dataAccessor: ({invoice}) => (
                                    (new Date(invoice.toDate).getMonth() === new Date().getMonth()
                                    && new Date(invoice.toDate).getFullYear() === new Date().getFullYear()) 
                                    ? <div>
                                        <Link url='/Excel/Invoice/-1' targetBlank>
                                            <FontAwesomeIcon icon={faFileExcel} />
                                            Gögn í Excel
                                        </Link>
                                    </div>
                                    : <div>
                                        <Link url={`/Excel/Invoice/${invoice.id}`} targetBlank>
                                            <FontAwesomeIcon icon={faFileExcel} />
                                            Gögn í Excel
                                        </Link>
                                    </div>
                                    ),
                                    textAlign: 'right',
                                }]}
                                underRowComponent={({invoice}) => {
                                    const subTableOption = reikningarToSubTableMap[invoice.id];
                                    if (subTableOption === 'Notendur') {
                                        const invoiceUserData = invoiceIdToInvoiceUserMap[invoice.id];
                                        if (invoiceUserData === undefined) {
                                            return null
                                        } else if (invoiceUserData instanceof Error) {
                                            return (
                                                <tr className='section__table__sub-table'>
                                                    <td colSpan={8} className='full-width-td'>
                                                        <div className='section__table__sub-table__inner'>
                                                            <ErrorAlert error={invoiceUserData} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return (
                                            <tr className='section__table__sub-table'>
                                                <td colSpan={8} className='full-width-td'>
                                                    <div className='section__table__sub-table__inner'>
                                                        <Table
                                                            data={invoiceUserData}
                                                            tableSize='lg'
                                                            columns={[{
                                                                title: 'Notandi',
                                                                dataAccessor: ({userName}) => userName,
                                                                textAlign: 'left'
                                                            }, {
                                                                title: 'Dagsetning',
                                                                dataAccessor: ({invoiceDate}) => getTableDate(invoiceDate.toString(), 'DD/MM/YYYY', '.')
                                                            }, {
                                                                title: 'Verð',
                                                                dataAccessor: ({amountTotal}) => formatNumber(amountTotal, '-', 0, ' kr.')
                                                            }, {
                                                                title: 'Verð án vsk.',
                                                                dataAccessor: ({amount}) => formatNumber(amount, '-', 0, ' kr.')
                                                            }, {
                                                                title: 'Vsk.',
                                                                dataAccessor: ({amountVat}) => formatNumber(amountVat, '-', 0, ' kr.')
                                                            }, {
                                                                title: 'Skýrslur',
                                                                dataAccessor: (invoiceUser) => (
                                                                    <Button 
                                                                        size='sm'
                                                                        buttonType='secondary'
                                                                        showLoader={(
                                                                            invoiceModalInfo?.modalType === 'Notendur'
                                                                            && invoiceUser === invoiceModalInfo.invoiceUser
                                                                            && invoiceUserDetailsMap[getUserDetailsMapId(invoiceModalInfo.invoiceUser)] === undefined
                                                                        )}
                                                                        onClick={() => {
                                                                            setInvoiceModalInfo({
                                                                                modalType: 'Notendur',
                                                                                invoiceUser
                                                                            })
                                                                        }}
                                                                    >
                                                                        Skoða
                                                                    </Button>
                                                                )
                                                            }]}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    } else if (subTableOption === 'Tilvisun') {
                                        const invoiceReferenceData = invoiceIdToInvoiceReferenceMap[invoice.id];
                                        if (invoiceReferenceData === undefined) {
                                            return null
                                        } else if (invoiceReferenceData instanceof Error) {
                                            return (
                                                <tr className='section__table__sub-table'>
                                                    <td colSpan={8} className='full-width-td'>
                                                        <div className='section__table__sub-table__inner'>
                                                            <ErrorAlert error={invoiceReferenceData} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }

                                        return (
                                            <tr className='section__table__sub-table'>
                                                <td colSpan={8} className='full-width-td'>
                                                    <div className='section__table__sub-table__inner'>
                                                        <Table
                                                            data={invoiceReferenceData}
                                                            tableSize='lg'
                                                            columns={[{
                                                                title: 'Tilvísun',
                                                                dataAccessor: ({ref}) => ref,
                                                                textAlign: 'left'
                                                            }, {
                                                                title: 'Verð',
                                                                dataAccessor: ({totalWithVat}) => formatNumber(totalWithVat, '-', 0, ' kr.')
                                                            }, {
                                                                title: 'Verð án vsk.',
                                                                dataAccessor: ({total}) => formatNumber(total, '-', 0, ' kr.')
                                                            }, {
                                                                title: 'Vsk.',
                                                                dataAccessor: ({vat}) => formatNumber(vat, '-', 0, ' kr.')
                                                            }, {
                                                                title: 'Skýrslur',
                                                                dataAccessor: (invoiceReference) => (
                                                                    <Button 
                                                                        size='sm'
                                                                        buttonType='secondary'
                                                                        onClick={() => {
                                                                            setInvoiceModalInfo({
                                                                                modalType: 'Tilvisun',
                                                                                invoiceReference
                                                                            })
                                                                        }}
                                                                    >
                                                                        Skoða
                                                                    </Button>
                                                                )
                                                            }]}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    }
                                    return null;
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Reikningar;
